import { createSlice } from '@reduxjs/toolkit';
import {
  changeOrderCourier,
  deleteProofOfDelivery,
  fetchAvailableCouriers,
  fetchDrivers,
  fetchOrderDetails,
  fetchOrderWarehouses,
  uploadProofOfDelivery,
} from './orderDetailsThunks';

const initialState = {
  data: null,
  loading: false,
  error: null,
  /** Why the order itself could not be loaded; `error` also carries a failed courier change. */
  loadError: null,
  activeTab: 'general',

  /** Couriers the company may route through, loaded once per session. */
  couriers: [],
  couriersLoaded: false,

  /** Options for the shipping editor, loaded when the modal first opens. */
  warehouseOptions: [],
  loadingWarehouses: false,
  driverOptions: [],
  driversLoaded: false,
  /** Why the drivers could not be loaded; without it the select said "Cargando..." for good. */
  driversError: null,
  /**
   * Whose fleet `driverOptions` is (the staff picks an operator), and the
   * request that may still change it. Another operator's list must never sit
   * under this one's name, and a late answer for the previous one is dropped.
   */
  driversAdminId: null,
  driversRequestId: null,

  /**
   * Proof-of-delivery images staged for the next status change. They are
   * uploaded as they are picked, so the list holds stored URLs, not files.
   */
  proofImages: [],
  uploadingProof: false,
  proofError: null,
};

const orderDetailsSlice = createSlice({
  name: 'orderDetails',
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    /** Drops the images staged for a status change without deleting them remotely. */
    clearProofImages: (state) => {
      state.proofImages = [];
      state.proofError = null;
    },
    clearOrderDetails: (state) => {
      state.data = null;
      state.error = null;
      state.loadError = null;
      state.activeTab = 'general';
      state.proofImages = [];
      state.proofError = null;
      // Belongs to the seller of the order just left, never to the next one.
      state.warehouseOptions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrderDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.loadError = null;
      })
      .addCase(fetchOrderDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchOrderDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.loadError = action.payload;
      })

      .addCase(fetchAvailableCouriers.fulfilled, (state, action) => {
        state.couriers = action.payload;
        state.couriersLoaded = true;
      })

      .addCase(fetchOrderWarehouses.pending, (state) => {
        state.loadingWarehouses = true;
      })
      .addCase(fetchOrderWarehouses.fulfilled, (state, action) => {
        state.loadingWarehouses = false;
        state.warehouseOptions = action.payload;
      })
      .addCase(fetchOrderWarehouses.rejected, (state) => {
        state.loadingWarehouses = false;
        state.warehouseOptions = [];
      })

      .addCase(fetchDrivers.pending, (state, action) => {
        const adminId = action.meta.arg?.adminId ?? null;
        state.driversError = null;
        state.driversRequestId = action.meta.requestId;
        if (String(adminId) !== String(state.driversAdminId)) {
          state.driversAdminId = adminId;
          state.driverOptions = [];
          state.driversLoaded = false;
        }
      })
      .addCase(fetchDrivers.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.driversRequestId) return;
        state.driverOptions = action.payload;
        state.driversLoaded = true;
      })
      .addCase(fetchDrivers.rejected, (state, action) => {
        if (action.meta.requestId !== state.driversRequestId) return;
        state.driversError = action.payload || 'No se pudieron cargar los conductores';
      })

      .addCase(uploadProofOfDelivery.pending, (state) => {
        state.uploadingProof = true;
        state.proofError = null;
      })
      .addCase(uploadProofOfDelivery.fulfilled, (state, action) => {
        state.uploadingProof = false;
        state.proofImages = [...state.proofImages, ...action.payload];
      })
      .addCase(uploadProofOfDelivery.rejected, (state, action) => {
        state.uploadingProof = false;
        state.proofError = action.payload;
      })
      .addCase(deleteProofOfDelivery.fulfilled, (state, action) => {
        state.proofImages = state.proofImages.filter((url) => url !== action.payload);
      })

      // A courier change rewrites the order, and `changeOrderCourier` already
      // refetched it; only the error has to reach the component from here.
      .addCase(changeOrderCourier.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { setActiveTab, clearProofImages, clearOrderDetails } = orderDetailsSlice.actions;
export default orderDetailsSlice.reducer;
